import React, { useEffect, useId, useState } from "react";
import clsx from "clsx";
import {
  ConsentManagementVariant,
  CustomConsentManagementVariant,
} from "../../../../../../common/service/server/dto/CMPSettingsDto";
import { Modal, SelectMenuDropdown, ToolTip, Button as AquiloButton } from "@truevault/aquilo";
import { OverrideableRegion } from "../../../../superorg/settings/ConsentBannerSettings";
import { InformationCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SelectMenuDropdownItem } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { Iso3166Region } from "../../../../../../common/service/server/Types";
import { ConsentRadioControlled } from "../../../consentBanner/ConsentRadio";
import { VARIANT_TOOLTIPS } from "./ConsentManagementVariantRow";
import { ConsentTable, ConsentTableColumn } from "../../../consentBanner/ConsentTable";

/**
 * Kind of a long story. The data model for the custom regions is pretty complicated, so we can't map it directly to a
 * `field` that radio groups would understand. Additionally, the custom regions are grouped by consent variant (each is
 * a row). So, we have to manually manage the mapping from the way we store the custom region data on the backend like
 * this:
 *
 * [{"region":"US-AL","variant":"NONE"},{"region":"US-AK","variant":"NONE"},{"region":"US-AZ","variant":"INTERACTIVE_DEFAULT_NO_CONSENT"},...]
 *
 * to a way we can represent it as data for the grouped-by-variant-per-row table like this:
 *
 * [{"id":"variant-row-0","regions":["US-AL","US-AK"],"variant":"NONE"},{"id":"variant-row-1","regions":["US-AZ"],"variant":"INTERACTIVE_DEFAULT_NO_CONSENT"},...]
 *
 * This happens at the boundary of this component: the incoming `customVariants` prop and the `onUpdate` callback expect
 * custom variant data in the first format, so we convert to the 2nd format for display & interaction in the table, then
 * convert back when updating.
 *
 * Because we aren't storing information about the table layout this doesn't support multiple rows with the same consent
 * variant - they get magically merged into one row. So we have a dialog that warns the user they'll be visually merged,
 * if they try to switch a row to have the same variant as an existing row.
 */
export interface ConsentManagementVariantClientRow {
  id: string; // Unique identifier for the row
  regions: string[]; // List of selected regions
  variant: ConsentManagementVariant; // Selected consent management variant
}

// Reusable function to map ConsentManagementVariantClientRow to CustomConsentManagementVariant
const clientRowsToCustomVariants = (
  rows: ConsentManagementVariantClientRow[],
): CustomConsentManagementVariant[] => {
  return rows.flatMap(({ regions, variant }) =>
    regions.map((region) => ({
      region,
      variant,
    })),
  );
};

// Reusable function to transform CustomConsentManagementVariant to ConsentManagementVariantClientRow
const customVariantsToClientRows = (
  variants: CustomConsentManagementVariant[],
  addFakeRow: boolean = false,
  allowInformationalBanner: boolean = true,
): ConsentManagementVariantClientRow[] => {
  const groupedByVariant = variants.reduce<Record<string, string[]>>((acc, { region, variant }) => {
    if (!acc[variant]) {
      acc[variant] = [];
    }
    acc[variant].push(region);
    return acc;
  }, {});

  const rows = Object.entries(groupedByVariant).map(([variant, regions], index) => ({
    id: `variant-row-${index}`,
    regions,
    variant: variant as ConsentManagementVariant,
  }));

  if (addFakeRow) {
    const candidateVariants = Object.values(ConsentManagementVariant).filter(
      (v) => allowInformationalBanner || v !== ConsentManagementVariant.INFORMATIONAL_ONLY,
    );
    rows.push({
      id: `variant-row-${rows.length}`,
      regions: [],
      variant:
        candidateVariants.find((v) => !rows.some((row) => row.variant === v)) ||
        ConsentManagementVariant.NONE,
    });
  }

  return rows;
};

// The rows for some custom variants, or one blank starter row when there are none.
const toClientRowsOrStarterRow = (
  variants: CustomConsentManagementVariant[] | undefined,
  addFakeRow: boolean,
  allowInformationalBanner: boolean,
): ConsentManagementVariantClientRow[] =>
  variants && variants.length > 0
    ? customVariantsToClientRows(variants, addFakeRow, allowInformationalBanner)
    : [{ id: "variant-row-0", regions: [], variant: ConsentManagementVariant.NONE }];

interface ConsentManagementVariantCustomRowProps {
  tableHeader: string;
  customVariants?: CustomConsentManagementVariant[];
  allowedRegions: OverrideableRegion[];
  onUpdate: (updatedRows: CustomConsentManagementVariant[]) => void;
  // When false, hide the Informational column (e.g. for Global Consent which only supports OPT-IN/OPT-OUT/NONE).
  allowInformationalBanner?: boolean;
  // Prefix stripped from region codes when displayed in the merge confirmation dialog (e.g. "US-" for state codes).
  regionDisplayPrefix?: string;
  // Placeholder shown in the SelectMenu when no regions are picked yet.
  selectPlaceholder?: string;
  // Label for the "add row" button (defaults to "Add another row").
  addButtonLabel?: string;
  // Visually conjoin this table's top edge with a sibling table directly above it.
  joinedTop?: boolean;
  // Show a search input at the top of the region picker dropdown.
  searchable?: boolean;
  searchPlaceholder?: string;
  // Show explanatory info icons next to the Opt-in/Opt-out/Informational column headers.
  showTooltip?: boolean;
}

export const ConsentManagementVariantCustomRow = ({
  tableHeader,
  customVariants,
  allowedRegions,
  onUpdate,
  allowInformationalBanner = true,
  regionDisplayPrefix = "US-",
  selectPlaceholder = "Choose states",
  addButtonLabel = "Add another row",
  joinedTop = false,
  searchable = false,
  searchPlaceholder,
  showTooltip = false,
}: ConsentManagementVariantCustomRowProps) => {
  const [isAddingRow, setIsAddingRow] = useState(false);
  // Built on the first render too, so the table never flashes its empty state.
  const [localRows, setLocalRows] = useState<ConsentManagementVariantClientRow[]>(() =>
    toClientRowsOrStarterRow(customVariants, false, allowInformationalBanner),
  );
  const [pendingChange, setPendingChange] = useState<{
    rowId: string;
    variant: ConsentManagementVariant;
  } | null>(null);
  // Row ids repeat across tables (both start at "variant-row-0"), so prefix radio names with this table's id: native
  // radios that share a name act as one group even across tables.
  const radioNamePrefix = useId();

  useEffect(() => {
    // Update `localRows` when `customVariants` prop changes
    setLocalRows(toClientRowsOrStarterRow(customVariants, isAddingRow, allowInformationalBanner));
  }, [customVariants, isAddingRow, allowInformationalBanner]);

  const regionOptions: SelectMenuDropdownItem<Iso3166Region>[] = allowedRegions.map((region) => ({
    label: region.label,
    value: region.region,
    previewLabel: region.abbreviation,
  }));

  const handleRegionChange = (rowId: string, region: string) => {
    setIsAddingRow(false);
    const updatedRows = localRows.map((row) =>
      row.id === rowId
        ? {
            ...row,
            regions: row.regions.includes(region)
              ? row.regions.filter((r) => r !== region) // Remove region if already selected
              : [...row.regions, region], // Add region if not selected
          }
        : row,
    );
    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows)); // Use reusable function
  };

  const handleVariantChange = (rowId: string, variant: ConsentManagementVariant) => {
    const existingRow = localRows.find((row) => row.variant === variant && row.id !== rowId);

    if (existingRow) {
      setPendingChange({ rowId, variant });
    } else {
      applyVariantChange(rowId, variant);
    }
  };

  const applyVariantChange = (rowId: string, variant: ConsentManagementVariant) => {
    setIsAddingRow(false);
    const updatedRows = localRows.map((row) => (row.id === rowId ? { ...row, variant } : row));
    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows));
  };

  const addRow = () => {
    setIsAddingRow(true);
    setLocalRows(customVariantsToClientRows(customVariants, true, allowInformationalBanner));
  };

  const removeRow = (rowId: string) => {
    setIsAddingRow(false);
    const updatedRows = localRows.filter((row) => row.id !== rowId);

    // Reset to one blank row if all rows are removed
    if (updatedRows.length === 0) {
      updatedRows.push({
        id: "variant-row-0",
        regions: [],
        variant: ConsentManagementVariant.NONE,
      });
    }

    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows)); // Use reusable function
  };

  const renderColumnHeader = (label: string, tooltipText: string) => (
    <div className="flex w-full items-center justify-center gap-[4px] whitespace-nowrap">
      <span>{label}</span>
      {showTooltip && (
        <ToolTip text={tooltipText} maxWidth={"400px"}>
          <InformationCircleIcon
            className="h-[16px] w-[16px] cursor-pointer text-neutral-500"
            aria-label={`About ${label}`}
          />
        </ToolTip>
      )}
    </div>
  );

  const renderVariantRadio = (
    item: ConsentManagementVariantClientRow,
    variant: ConsentManagementVariant,
    label: string,
  ) => (
    <div className="flex justify-center">
      <ConsentRadioControlled
        name={`${radioNamePrefix}-override-${item.id}`}
        label={`${
          item.regions.length > 0
            ? item.regions.map((region) => region.replace(regionDisplayPrefix, "")).join(", ")
            : selectPlaceholder
        }: ${label}`}
        checked={localRows.find((row) => row.id === item.id)?.variant === variant}
        onChange={() => handleVariantChange(item.id, variant)}
        disabled={!(item.regions?.length > 0)}
      />
    </div>
  );

  // Radio columns get fixed widths when joined to the table above, so they line up with its columns.
  const radioColumnClassName = clsx("pl-[8px] pr-[8px] text-center", joinedTop && "w-[120px]");

  const columns: ConsentTableColumn<ConsentManagementVariantClientRow>[] = [
    {
      key: "region",
      header: tableHeader,
      className: "pr-[12px]",
      render: (item) => (
        <div className="flex items-center">
          <SelectMenuDropdown
            allowMultipleSelected={true}
            isVerticallyStacked={false}
            showAllSelected={true}
            noSelectedErrorMessage={selectPlaceholder}
            maxWidth="320px"
            dropdownWidthPx={360}
            menuMaxHeightPx={280}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
            items={regionOptions.map((option) => ({
              ...option,
              isSelected: item.regions.includes(option.value),
              disabledTooltip: "Already selected in another row",
              isDisabled: localRows.some(
                (row) => row.id !== item.id && row.regions.includes(option.value),
              ),
            }))}
            onSelect={async (value) => handleRegionChange(item.id, value)}
          />
        </div>
      ),
    },
    {
      key: "optin",
      header: renderColumnHeader("Opt-in", VARIANT_TOOLTIPS.optIn),
      className: radioColumnClassName,
      render: (item) =>
        renderVariantRadio(item, ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT, "Opt-in"),
    },
    {
      key: "optout",
      header: renderColumnHeader("Opt-out", VARIANT_TOOLTIPS.optOut),
      className: radioColumnClassName,
      render: (item) =>
        renderVariantRadio(item, ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT, "Opt-out"),
    },
    ...(allowInformationalBanner
      ? [
          {
            key: "informational",
            header: renderColumnHeader("Informational", VARIANT_TOOLTIPS.informational),
            className: radioColumnClassName,
            render: (item: ConsentManagementVariantClientRow) =>
              renderVariantRadio(
                item,
                ConsentManagementVariant.INFORMATIONAL_ONLY,
                "Informational",
              ),
          },
        ]
      : []),
    {
      key: "none",
      header: <div className="flex w-full justify-center whitespace-nowrap">None</div>,
      className: radioColumnClassName,
      render: (item) => renderVariantRadio(item, ConsentManagementVariant.NONE, "None"),
    },
    {
      key: "_actions",
      header: null,
      className: clsx("pl-[8px] pr-[8px] text-center", joinedTop && "w-[64px]"),
      render: (item) => (
        <div className="flex items-center justify-center">
          <AquiloButton
            color="tertiary"
            variant="round"
            isCircular
            onClick={() => removeRow(item.id)}
            disabled={localRows?.length === 1 && !(item.regions?.length > 0)}
          >
            <TrashIcon className="h-[20px] w-[20px] text-gray-500" />
            <span className="sr-only">Remove this row</span>
          </AquiloButton>
        </div>
      ),
    },
  ];

  const pendingRegions = localRows
    ?.find((row) => row.id === pendingChange?.rowId)
    ?.regions.map((r) => r.replace(regionDisplayPrefix, ""))
    .join(", ");
  const existingRegions = localRows
    ?.find((row) => row.variant === pendingChange?.variant && row.id !== pendingChange?.rowId)
    ?.regions.map((r) => r.replace(regionDisplayPrefix, ""))
    .join(", ");

  return (
    <>
      {pendingChange && (
        <Modal
          title="Combine override rows?"
          body={`You're changing the banner override for: ${pendingRegions}. There's already a row with that banner type for: ${existingRegions}. Because they'll share a banner type, the rows will be combined.`}
          alignText="left"
          alignButton="right"
          closeButton={true}
          onClose={() => setPendingChange(null)}
          firstButton="Don't combine"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setPendingChange(null)}
          secondButton="Combine"
          secondButtonColor="primary"
          onSecondButtonClick={() => {
            // The transform functions handle "merging" and removing the old row, all we have to do here is call apply.
            applyVariantChange(pendingChange.rowId, pendingChange.variant);
            setPendingChange(null);
          }}
          showOverlay={true}
          closeOnOverlayClick={false}
        />
      )}
      <div className="w-full">
        <ConsentTable<ConsentManagementVariantClientRow>
          joinedTop={joinedTop}
          emptyState={
            <p className="m-0 text-[14px] text-neutral-600">
              No overrides yet. Add one to get started.
            </p>
          }
          keyExtractor={(item) => item.id}
          columns={columns}
          data={localRows}
        />
        {
          // Hide the button when:
          // (1) we're mid-add, (2) the row cap is reached, or
          // (3) no overrides are saved yet — the starter "Choose states/countries" row covers
          //     this case, and adding another would feed undefined into customVariantsToClientRows.
          !(
            isAddingRow ||
            localRows?.length >= (allowInformationalBanner ? 4 : 3) ||
            !customVariants?.length
          ) && (
            <div className="mt-[12px]">
              <AquiloButton onClick={addRow} color="tertiary" size="s">
                <PlusIcon className="mr-2 h-[20px] w-[20px] text-gray-500" />
                {addButtonLabel}
              </AquiloButton>
            </div>
          )
        }
      </div>
    </>
  );
};
